<template>
  <div class="home general">
    <h1 class="section-title">Canción y género</h1>

    <div class="filters">
      <va-input v-model="since" type="date" label="Desde" />
      <va-input v-model="until" type="date" label="Hasta" />
      <va-input v-model="num" type="number" label="Número de canción" />
      <va-button color="info" :disabled="isLoading" @click="loadReport">
        Consultar
      </va-button>
    </div>

    <p v-if="error" class="analytics-error">{{ error }}</p>
    <p v-if="isLoading" class="analytics-status">Cargando canción...</p>
    <p v-if="!report && !isLoading && !error" class="analytics-status">
      Ingresa el número de la canción para ver su comportamiento y el de su género.
    </p>

    <div v-if="report" class="summary">
      <div class="summary-item">
        <span>Número</span>
        <strong>{{ report.song.song_num }}</strong>
      </div>
      <div class="summary-item">
        <span>Canción</span>
        <strong>
          <router-link
            v-if="report.song.songId"
            class="table-span linked"
            :to="{ name: 'songDetail', params: { id: report.song.songId } }"
          >
            {{ report.song.song_title }}
          </router-link>
          <span v-else>{{ report.song.song_title }}</span>
        </strong>
      </div>
      <div class="summary-item">
        <span>Artista</span>
        <strong>
          <router-link
            v-if="report.song.artist_id"
            class="table-span linked"
            :to="{ name: 'artistDetail', params: { id: report.song.artist_id } }"
          >
            {{ report.song.artist_name }}
          </router-link>
          <span v-else>{{ report.song.artist_name }}</span>
        </strong>
      </div>
      <div class="summary-item">
        <span>Género</span>
        <strong>
          <router-link
            v-if="report.song.genre_id"
            class="table-span linked"
            :to="{ name: 'genreDetail', params: { id: report.song.genre_id } }"
          >
            {{ report.song.genre_name }}
          </router-link>
          <span v-else>{{ report.song.genre_name }}</span>
        </strong>
      </div>
      <div class="summary-item">
        <span>Ranking catálogo</span>
        <strong>{{ formatRank(report.catalog.rank, report.catalog.unique_songs) }}</strong>
      </div>
      <div class="summary-item">
        <span>Ranking en el género</span>
        <strong>{{ formatRank(report.rank_in_genre, report.songs_in_genre) }}</strong>
      </div>
    </div>

    <h2 v-if="report" class="section-title">Canción</h2>
    <div v-if="report" class="summary">
      <div class="summary-item">
        <span>Reproducciones</span>
        <strong>{{ report.song_summary.plays }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios</span>
        <strong>{{ report.song_summary.unique_users }}</strong>
      </div>
      <div class="summary-item">
        <span>Repros. por usuario</span>
        <strong>{{ formatNumber(report.song_summary.plays_per_user) }}</strong>
      </div>
      <div class="summary-item">
        <span>Participación catálogo</span>
        <strong>{{ formatPercent(report.song_summary.catalog_share) }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios que vuelven</span>
        <strong>{{ formatPercent(report.song_summary.repeat_user_rate) }}</strong>
      </div>
      <div class="summary-item">
        <span>Tasa de completar</span>
        <strong>{{ formatPercent(report.song_summary.completion_rate) }}</strong>
      </div>
      <div class="summary-item">
        <span>Con licencia</span>
        <strong>{{ formatPercent(report.song_summary.licensed_rate) }}</strong>
      </div>
      <div class="summary-item">
        <span>Puntaje medio</span>
        <strong>{{ formatNumber(report.song_summary.avg_score) }}</strong>
      </div>
      <div class="summary-item">
        <span>% reproducido</span>
        <strong>{{ formatPlayedPercent(report.song_summary.avg_played_percentage) }}</strong>
      </div>
      <div class="summary-item">
        <span>Segundos medios</span>
        <strong>{{ formatNumber(report.song_summary.avg_played_seconds) }}</strong>
      </div>
      <div class="summary-item">
        <span>Hora pico</span>
        <strong>{{ report.song_summary.peak_hour_label || "-" }}</strong>
      </div>
      <div class="summary-item">
        <span>Día pico</span>
        <strong>{{ report.song_summary.peak_weekday || "-" }}</strong>
      </div>
    </div>

    <h2 v-if="report" class="section-title">Género</h2>
    <div v-if="report" class="summary">
      <div class="summary-item">
        <span>Reproducciones</span>
        <strong>{{ report.genre_summary.plays }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios</span>
        <strong>{{ report.genre_summary.unique_users }}</strong>
      </div>
      <div class="summary-item">
        <span>Canciones distintas</span>
        <strong>{{ report.genre_summary.unique_songs }}</strong>
      </div>
      <div class="summary-item">
        <span>Repros. por usuario</span>
        <strong>{{ formatNumber(report.genre_summary.plays_per_user) }}</strong>
      </div>
      <div class="summary-item">
        <span>Participación catálogo</span>
        <strong>{{ formatPercent(report.genre_summary.catalog_share) }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios que vuelven</span>
        <strong>{{ formatPercent(report.genre_summary.repeat_user_rate) }}</strong>
      </div>
      <div class="summary-item">
        <span>Tasa de completar</span>
        <strong>{{ formatPercent(report.genre_summary.completion_rate) }}</strong>
      </div>
      <div class="summary-item">
        <span>Puntaje medio</span>
        <strong>{{ formatNumber(report.genre_summary.avg_score) }}</strong>
      </div>
      <div class="summary-item">
        <span>Hora pico</span>
        <strong>{{ report.genre_summary.peak_hour_label || "-" }}</strong>
      </div>
      <div class="summary-item">
        <span>Día pico</span>
        <strong>{{ report.genre_summary.peak_weekday || "-" }}</strong>
      </div>
    </div>

    <h2 v-if="report" class="section-title">Canción vs género</h2>
    <va-data-table
      v-if="report"
      class="ekitable"
      :items="report.comparison"
      :columns="comparisonColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(song)="{ rowData }">
        {{ formatMetric(rowData.song, rowData.format) }}
      </template>
      <template #cell(genre)="{ rowData }">
        {{ formatMetric(rowData.genre, rowData.format) }}
      </template>
    </va-data-table>

    <h2 v-if="report" class="section-title">Por hora</h2>
    <va-data-table
      v-if="report"
      class="ekitable"
      :items="report.hours"
      :columns="hourColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(share)="{ rowData }">
        {{ formatPercent(rowData.share) }}
      </template>
    </va-data-table>

    <h2 v-if="report" class="section-title">Por día de semana</h2>
    <va-data-table
      v-if="report"
      class="ekitable"
      :items="report.weekdays"
      :columns="weekdayColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(share)="{ rowData }">
        {{ formatPercent(rowData.share) }}
      </template>
    </va-data-table>

    <h2 v-if="report" class="section-title">Por día</h2>
    <va-data-table
      v-if="report"
      class="ekitable"
      :items="report.by_day"
      :columns="dayColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    />

    <h2 v-if="report" class="section-title">Usuarios de la canción</h2>
    <va-data-table
      v-if="report"
      class="ekitable"
      :items="report.users"
      :columns="userColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(avg_score)="{ rowData }">
        {{ formatNumber(rowData.avg_score) }}
      </template>
    </va-data-table>

    <h2 v-if="report" class="section-title">Otras canciones del género</h2>
    <va-data-table
      v-if="report"
      class="ekitable"
      :items="report.genre_songs"
      :columns="genreSongColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(song_title)="{ rowData }">
        <router-link
          v-if="rowData.songId"
          class="table-span linked"
          :to="{ name: 'songDetail', params: { id: rowData.songId } }"
        >
          {{ rowData.song_title }}
        </router-link>
        <span v-else>{{ rowData.song_title }}</span>
      </template>
    </va-data-table>
  </div>
</template>

<script>
import requester from "@/services/requester";

function currentMonthRange() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const lastDay = String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, "0");
  return {
    since: `${now.getFullYear()}-${month}-01`,
    until: `${now.getFullYear()}-${month}-${lastDay}`,
  };
}

export default {
  name: "SongBehavior",
  data() {
    const month = currentMonthRange();
    return {
      num: "",
      since: month.since,
      until: month.until,
      isLoading: false,
      error: "",
      report: null,
      comparisonColumns: [
        { key: "metric", sortable: false, label: "Métrica" },
        { key: "song", sortable: false, label: "Canción" },
        { key: "genre", sortable: false, label: "Género" },
      ],
      hourColumns: [
        { key: "hour_label", sortable: false, label: "Hora" },
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
        { key: "share", sortable: false, label: "Participación" },
      ],
      weekdayColumns: [
        { key: "weekday_name", sortable: false, label: "Día" },
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
        { key: "share", sortable: false, label: "Participación" },
      ],
      dayColumns: [
        { key: "day_label", sortable: false, label: "Día" },
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
      ],
      userColumns: [
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "user_name", sortable: false, label: "Usuario" },
        { key: "avg_score", sortable: false, label: "Puntaje medio" },
      ],
      genreSongColumns: [
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "song_num", sortable: false, label: "N°" },
        { key: "song_title", sortable: false, label: "Canción" },
        { key: "artist_name", sortable: false, label: "Artista" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
      ],
    };
  },
  methods: {
    formatNumber(value) {
      if (value == null || value === "") {
        return "-";
      }
      return Number(value).toFixed(1);
    },
    formatPercent(value) {
      if (value == null || value === "") {
        return "-";
      }
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    formatPlayedPercent(value) {
      if (value == null || value === "") {
        return "-";
      }
      return `${Number(value).toFixed(1)}%`;
    },
    formatRank(rank, total) {
      if (rank == null || rank === "") {
        return "-";
      }
      if (total) {
        return `${rank} de ${total}`;
      }
      return String(rank);
    },
    formatMetric(value, format) {
      if (format === "rate") {
        return this.formatPercent(value);
      }
      if (format === "percent") {
        return this.formatPlayedPercent(value);
      }
      if (format === "count") {
        if (value == null || value === "") {
          return "-";
        }
        return String(value);
      }
      return this.formatNumber(value);
    },
    async loadReport() {
      const songNum = parseInt(this.num, 10);
      if (!songNum || songNum < 1) {
        this.error = "Ingresa el número de la canción.";
        this.report = null;
        return;
      }
      this.isLoading = true;
      this.error = "";
      try {
        const query = [`num=${songNum}`];
        if (this.since) {
          query.push(`since=${this.since}`);
        }
        if (this.until) {
          query.push(`until=${this.until}`);
        }
        const data = await requester.Get(`/song-behavior?${query.join("&")}`, true);
        if (data.status === 200 && data.data) {
          this.report = data.data;
        } else if (data.status === 404) {
          this.report = null;
          this.error = "No hay canción con ese número.";
        } else {
          this.report = null;
          this.error = "No se pudo cargar el comportamiento de la canción.";
        }
      } catch (e) {
        console.log(e);
        this.report = null;
        if (e.response && e.response.status === 404) {
          this.error = "No hay canción con ese número.";
        } else {
          this.error = "No se pudo cargar el comportamiento de la canción.";
        }
      }
      this.isLoading = false;
    },
  },
  created() {
    if (this.$route.query.num) {
      this.num = this.$route.query.num;
      this.loadReport();
    }
  },
};
</script>

<style lang="scss" scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 0 15px 20px;
}

.summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 15px 20px;
}

.summary-item {
  background: #123d62;
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.analytics-error,
.analytics-status {
  padding: 0 15px 16px;
}

.analytics-error {
  color: #f8b4b4;
}
</style>
